<template>
    <modal
        v-if="show && showV1Modal"
        :form="form"
        :type="'is-primary'"
        :title="titleModal"
        :hide-foot="hideFooter"
        :success-text="successTextModal"
        :submit-text="submitTextModal"
        :titleIcon="titleArrowBack"
        class="upgradePlan__modal"
        :class="{
            'upgradePlan__modal-old': checkOldBillingPlans,
            'upgradePlan__modal-downgrade': useDowngrade,
        }"
        :width="modalWidth"
        :titleChange="handleBack"
        @closed="handleClose"
        @submit="handleSubmit"
    >
        <template v-if="!useDowngrade">
            <template v-if="step === STEPS.UPGRADE_OPPORTUNITIES">
                <upgrade-opportunities
                    @go-to-choose-plans="setStep(STEPS.UPGRADE_CHOOSE_PLANS)"
                    @go-to-upgrade-plan="goToUpgradePlan"
                    @on-close-modal="handleClose"
                />
            </template>
            <template v-if="step === STEPS.UPGRADE_CHOOSE_PLANS">
                <upgrade-section
                    ref="upgradeSection"
                    :form="form"
                    :hasPlanDowngrade="hasPlanDowngrade"
                    :showPreviewPlan="showPreviewPlan"
                    :plan="plan"
                    :type="type"
                    :price="price"
                    :has-card="hasCard"
                    @handle-change-data="handleChangeData"
                    @go-to-opportunities="setStep(STEPS.UPGRADE_CHOOSE_PLANS)"
                    @close="handleClose"
                />
            </template>
        </template>

        <template v-if="useDowngrade">
            <downgrade-section
                :isCanceled="
                    subscriptionStatus === 'canceled' || subscriptionStatus === 'incomplete_expired'
                "
                @handle-change-data="handleChangeData"
                @close="handleClose"
            />
        </template>
    </modal>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Form from 'core/Form';
import UpgradeSection from './UpgradeSection.vue';
import DowngradeSection from './DowngradeSection.vue';
import UpgradeOpportunities from './UpgradeOpportunities/UpgradeOpportunities.vue';
import EarlyUpgradeMixin from 'mixins/early-upgrade';
import OrganizationMixin from 'mixins/organization';
import SourcesMixin from 'mixins/cards/sources';

const STEPS = {
    UPGRADE_OPPORTUNITIES: 1,
    UPGRADE_CHOOSE_PLANS: 2,
    UPGRADE_PLAN: 3,
};

export default {
    emits: ['close'],
    
    components: {
        UpgradeSection,
        DowngradeSection,
        UpgradeOpportunities,
    },

    mixins: [EarlyUpgradeMixin, OrganizationMixin, SourcesMixin],

    props: {
        show: { type: Boolean, default: false },
        useDowngrade: { type: Boolean, default: false },
    },

    data() {
        return {
            STEPS: STEPS,

            step: STEPS.UPGRADE_OPPORTUNITIES,
            form: new Form({}),
            hideFooter: true,
            hasPlanDowngrade: false,
            showPreviewPlan: false,
            isBackOpportunities: false,

            plan: '',
            type: '',
            price: 0,
            hasCard: false,
        };
    },

    computed: {
        titleArrowBack() {
            if (this.showPreviewPlan) return ['far', 'chevron-left'];

            if (this.hasSuggestion) {
                if (this.step === STEPS.UPGRADE_CHOOSE_PLANS) return ['far', 'chevron-left'];
            }

            return false;
        },

        titleModal() {
            if (this.useDowngrade || this.hasPlanDowngrade) {
                return 'Downgrade';
            }

            return 'Upgrade';
        },

        showV1Modal() {
            return !this.featureFlags?.billing_upgrade_v2;
        },

        successTextModal() {
            if (this.useDowngrade || this.hasPlanDowngrade) {
                return 'Downgrade Successful';
            }

            return 'Upgrade Successful';
        },

        submitTextModal() {
            if (this.useDowngrade || this.hasPlanDowngrade) {
                return 'Downgrade Plan';
            }

            return 'Submit';
        },

        modalWidth() {
            if (this.step === STEPS.UPGRADE_OPPORTUNITIES) {
                return 767;
            }

            return this.hideFooter ? 590 : 480;
        },

        hasSuggestion() {
            if (this.isOnTrial) return false;

            return Boolean(this.subscription.subscription_suggestion);
        },

        ...mapState(stores.Subscriptions, ['subscription']),
    },

    watch: {
        show(status) {
            if (status) {
                this.init();
            } else {
                this.initReset();
            }
        },

        showPreviewPlan(status) {
            this.hideFooter = !status;
        },
    },

    methods: {
        init() {
            this.initStep();
        },

        initStep() {
            this.step = this.hasSuggestion
                ? STEPS.UPGRADE_OPPORTUNITIES
                : STEPS.UPGRADE_CHOOSE_PLANS;
        },

        initReset() {
            this.form.reset();
            this.hideFooter = true;
            this.hasPlanDowngrade = false;
            this.showPreviewPlan = false;

            this.plan = '';
            this.type = '';
            this.price = '';
            this.hasCard = false;

            this.initStep();
        },

        setStep(step) {
            this.step = step;
        },

        goToUpgradePlan({ plan }) {
            this.plan = plan.id;
            this.type = plan.id;
            this.price = plan.price;
            this.showPreviewPlan = true;
            this.hideFooter = false;
            this.isBackOpportunities = true;

            this.hasCard = !(!this.sortedBillingSources.length || this.isCancelled);

            this.setStep(STEPS.UPGRADE_CHOOSE_PLANS);
        },

        handleClose() {
            this.showPreviewPlan = false;
            this.hideFooter = true;
            this.hideEarlyDowngrade();
            this.$emit('close');
        },

        handleSubmit() {
            if (this.$refs.upgradeSection) {
                this.$refs.upgradeSection.$refs.changePlanNew.save();
            }
        },

        handleBack() {
            if (this.step === STEPS.UPGRADE_CHOOSE_PLANS) {
                if (this.showPreviewPlan) {
                    this.showPreviewPlan = false;

                    if (this.isBackOpportunities) {
                        this.isBackOpportunities = false;
                        this.setStep(STEPS.UPGRADE_OPPORTUNITIES);
                    }
                } else {
                    this.setStep(STEPS.UPGRADE_OPPORTUNITIES);
                }
            } else {
                this.hasPlanDowngrade = false;
            }
        },

        handleChangeData(data) {
            if (data[0] === 'form') {
                if (data[2] === 'record') {
                    this[data[0]][data[1]][data[2]](data[3]);

                    return;
                }

                this[data[0]][data[1]] = data[2];

                return;
            }

            this[data[0]] = data[1];
        },
    },
};
</script>
